import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoGameInfo } from '@ps/web-martech-cms-types';
import { takeUntil } from 'rxjs/operators';

import {
  getIcon,
  isFSB,
  preload,
  setLoading,
  transformParams,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { initialState } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';

export interface CasinoGameInfoWidgetState extends StreamData<CasinoGame> {
  loading: boolean;
}

const reducers = {
  set: (state: CasinoGameInfoWidgetState, payload: CasinoGameInfoWidgetState) => payload,
  setLoading
};

const effects = {
  preload,
  userUpdates,
  userLoggedIn,
  userLoggedOut,
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoGameInfo>) {
    const {
      actions,
      dispatch,
      request,
      requestStream,
      widget: { uid, params: body, game_service: service }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoGameInfoWidgetModel> = dispatch[uid];
    const options = { service, body: transformParams(body) };
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    widgetDispatch.setLoading(true);

    if (!canUseDOM) {
      const { body: data } = await request<CasinoGameInfoWidgetState>(options);
      widgetDispatch.set({ ...data, loading: false });
    } else {
      requestStream<CasinoGameInfoWidgetState>(options, undefined)
        .pipe(takeUntil(currencyChange(actions) as typeof actions), takeUntil(onLogout))
        .subscribe((update) => {
          widgetDispatch.set({ ...update, loading: false });
        });
    }
  }
};

const selectors = (slice: Slicer<CasinoGameInfoWidgetState>) => ({
  get() {
    return slice;
  },
  getIcon,
  isFSB
});

export interface CasinoGameInfoWidgetModel
  extends WidgetModelConfig<CasinoGameInfo, CasinoGameInfoWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const gameInfoWidgetModel: CasinoGameInfoWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useGameInfoWidgetModel: UseWidget<CasinoGameInfo, CasinoGameInfoWidgetModel> =
  useWidget;
